import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useMemo } from "react";
import type { LinksFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import { telemetry } from "#telemetry";
import { getCacheUserId } from "#user";
import sonnerStylesheet from "sonner/dist/styles.css?url";
import { LoadingScreen, loadingScreenStyles } from "./containers";
import { createAppQueryClient, createIdbPersister } from "./queryPersistence";
import tailwindStylesheet from "./tailwind.css?url";

// Injected by Vite (see vite.config.ts). Hash of every Zod schema file —
// changes whenever a persisted shape changes, busting the IndexedDB cache.
declare const __FATBEES_CACHE_BUSTER__: string | undefined;
export function getCacheBuster(): string {
  return typeof __FATBEES_CACHE_BUSTER__ === "string"
    ? __FATBEES_CACHE_BUSTER__
    : "dev";
}

// Extracted so it can be unit-tested; ScrollRestoration only invokes it on
// real navigation events, which we don't simulate.
export function getScrollRestorationKey(location: {
  pathname: string;
}): string {
  return location.pathname;
}

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStylesheet },
  // Sonner doesn't load its styles on mobile for some reason, so we
  // load it manually
  { rel: "stylesheet", href: sonnerStylesheet },
];

// Inline boot script. Runs synchronously, in <head>, before the bundle
// starts downloading. Brand-new visitors have nothing in `localStorage`
// for the cache user id key (set by setCacheUserId on login, cleared by
// clearCacheUserId on logout), so we redirect them straight to the
// prerendered /login page — that way they see the form on first paint
// instead of the HydrateFallback spinner.
//
// Returning visitors keep going through the React tree, which already
// handles redirecting to /login if their session is no longer valid.
//
// /dev/* pages (e.g. /dev/submissions) are deliberately open — firmware
// engineers use them without an account — so they're exempt here, and
// they live outside the `_app` layout so its auth redirect doesn't
// apply either.
//
// The "fatbees:cache-user-id" string is duplicated here from
// features/user/cacheUserId.ts because this script ships as a literal
// in the prerendered HTML — keep them in sync.
const earlyLoginRedirectScript = `
(function () {
  try {
    if (location.pathname === "/login") return;
    if (location.pathname.indexOf("/dev/") === 0) return;
    if (localStorage.getItem("fatbees:cache-user-id")) return;
    var returnTo = location.pathname + location.search;
    var url = returnTo === "/" || returnTo === ""
      ? "/login"
      : "/login?returnTo=" + encodeURIComponent(returnTo);
    location.replace(url);
  } catch (e) {}
})();
`;

function HtmlShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="min-h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{ __html: earlyLoginRedirectScript }}
        />
      </head>
      <body className="bg-white xs:bg-primary-50/70 min-h-full touch-pan-y">
        {children}
        <ScrollRestoration getKey={getScrollRestorationKey} />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const queryClient = useMemo(() => createAppQueryClient(), []);
  const persister = createIdbPersister(getCacheUserId());

  return (
    <HtmlShell>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, buster: getCacheBuster() }}
        onSuccess={() => {
          // Telemetry (docs/client-telemetry.md): what IndexedDB handed
          // back before any refetch — the per-query detail follows in the
          // "query cache snapshot" entry once the session starts.
          telemetry.info("persisted query cache restored", {
            queries: queryClient.getQueryCache().getAll().length,
            buster: getCacheBuster(),
          });
          queryClient.invalidateQueries();
        }}
      >
        <Outlet />
      </PersistQueryClientProvider>
    </HtmlShell>
  );
}

// Rendered into index.html at build time and shown while the JS bundle and
// main Tailwind stylesheet download on slow connections. The CSS lives in
// LoadingScreen.css (next to the React component) and is imported with
// `?inline` so Tailwind compiles the @apply'd utilities at build time and
// we embed the small result here as a <style> tag — no external CSS
// needed for the loader to look right. React Router swaps this out for
// the real app once hydration finishes.
//
// HydrateFallback renders the whole document (rather than just the body
// content) so we don't need to export `Layout` from the route. Exporting
// Layout changes how React Router builds the root route entry (`element:
// <Layout><Component/></Layout>` instead of `Component`), which we found
// regressed initial render timing in Firefox during E2E.
export function HydrateFallback() {
  return (
    <HtmlShell>
      <style dangerouslySetInnerHTML={{ __html: loadingScreenStyles }} />
      <LoadingScreen />
    </HtmlShell>
  );
}
